import React, { Component } from 'react';

import filter from 'lodash/filter';
import toLower from 'lodash/toLower';

import { IS_CLOUD } from '@qality/common-config/src/featureFlags';
import { messages } from '@qality/common-messages';
import {
  PermissionService,
  SettingsService,
  StorageOfUniqueServices,
} from '@qality/common-service';
import { Utils } from '@qality/common-utils';

import { QAlitySettingsContext } from './QAlitySettingsContext';

type Props = {
  children: any;
};

type State = {
  isJiraAdminUser: boolean;
  isAppVisibleInAllIssuesForAllProjects: boolean;
  isPluginEnabledForAllProjects: boolean;
  filteredProjects: Array<any>;
  loading: boolean;
  isInitialized: boolean;
  query: string;
};

export class QAlitySettingsContextProvider extends Component<Props, State> {
  allProjectsWithSettings = [];
  constructor(props: Props) {
    super(props);
    this.state = {
      isJiraAdminUser: false,
      isAppVisibleInAllIssuesForAllProjects: false,
      isPluginEnabledForAllProjects: false,
      filteredProjects: [],
      loading: true,
      isInitialized: false,
      query: '',
    };
  }

  async componentDidMount() {
    this.reloadSettings();
  }

  onFilterChange = (event) => {
    const query = event.currentTarget.value;
    const filteredProjects = this.getFilteredProjects(query);
    const isAppVisibleInAllIssuesForAllProjects =
      this.getIsAppVisibleInAllIssuesForGivenProjects(filteredProjects);
    const isPluginEnabledForAllProjects =
      this.getIsPluginEnabledForGivenProjects(filteredProjects);

    this.setState({
      filteredProjects,
      isAppVisibleInAllIssuesForAllProjects,
      isPluginEnabledForAllProjects,
      query,
    });
  };

  clearFilter = () => {
    const query = '';
    const filteredProjects = this.getFilteredProjects(query);
    const isAppVisibleInAllIssuesForAllProjects =
      this.getIsAppVisibleInAllIssuesForGivenProjects(filteredProjects);
    const isPluginEnabledForAllProjects =
      this.getIsPluginEnabledForGivenProjects(filteredProjects);

    this.setState({
      filteredProjects,
      isAppVisibleInAllIssuesForAllProjects,
      isPluginEnabledForAllProjects,
      query,
    });
  };

  getFilteredProjects(query) {
    if (Utils.isNilOrEmpty(query)) {
      return [...this.allProjectsWithSettings];
    } else {
      return filter(this.allProjectsWithSettings, (project) => {
        return toLower(project.name).includes(toLower(query));
      });
    }
  }

  toggleAppVisibilityInAllIssues = async (project, index) => {
    const filteredProjects = this.state.filteredProjects;
    filteredProjects[index].toggleVisibilityInAllIssues();
    const isAppVisibleInAllIssuesForAllProjects =
      this.getIsAppVisibleInAllIssuesForGivenProjects(filteredProjects);

    this.setState({
      filteredProjects,
      isAppVisibleInAllIssuesForAllProjects,
    });
    try {
      await SettingsService.updateQAlitySettings(filteredProjects[index]);
    } catch (e) {
      if (e.toJSON().status === 403 && IS_CLOUD) {
        StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
          e,
          messages['qalitySettings.error.setEnabled.403']
        );
      } else {
        StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
          e,
          messages['qalitySettings.error.setEnabled.general']
        );
      }
    }
  };

  togglePluginInProject = async (project, index) => {
    const filteredProjects = this.state.filteredProjects;
    filteredProjects[index].togglePluginInProject();
    const isPluginEnabledForAllProjects =
      this.getIsPluginEnabledForGivenProjects(filteredProjects);
    const isAppVisibleInAllIssuesForAllProjects =
      this.getIsAppVisibleInAllIssuesForGivenProjects(filteredProjects);

    this.setState({
      filteredProjects,
      isPluginEnabledForAllProjects,
      isAppVisibleInAllIssuesForAllProjects,
    });
    try {
      await SettingsService.updateQAlitySettings(filteredProjects[index]);
    } catch (e) {
      if (e.toJSON().status === 403 && IS_CLOUD) {
        StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
          e,
          messages['qalitySettings.error.isPluginEnabled.403']
        );
      } else {
        StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
          e,
          messages['qalitySettings.error.isPluginEnabled.general']
        );
      }
    }
  };

  toggleAppVisibilityInAllIssuesForAllProjects = () => {
    this.setState(
      {
        loading: true,
      },
      async () => {
        try {
          const isAppVisibleInAllIssuesForAllProjects =
            !this.state.isAppVisibleInAllIssuesForAllProjects;
          const filteredProjectsWithPluginEnabled =
            this.state.filteredProjects.filter((p) => p.isPluginEnabled);
          await SettingsService.updateAppVisibilityInAllProjects(
            filteredProjectsWithPluginEnabled,
            isAppVisibleInAllIssuesForAllProjects
          );
          this.state.filteredProjects.forEach((p) => {
            if (p.isPluginEnabled) {
              p.isAppVisibleInAllIssues = isAppVisibleInAllIssuesForAllProjects;
            }
          });

          this.setState({
            filteredProjects: this.state.filteredProjects,
            isAppVisibleInAllIssuesForAllProjects,
            loading: false,
          });
        } catch (e) {
          StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
            e,
            messages['error.setEnabledForFiltered.message']
          );
        }
      }
    );
  };

  togglePluginForAllProjects = () => {
    this.setState(
      {
        loading: true,
      },
      async () => {
        try {
          const filteredProjects = this.state.filteredProjects;
          const isPluginEnabledForAllProjects =
            !this.state.isPluginEnabledForAllProjects;
          await SettingsService.updatePluginEnabledInAllProjects(
            filteredProjects,
            isPluginEnabledForAllProjects
          );
          this.state.filteredProjects.forEach((filteredProject) => {
            filteredProject.isPluginEnabled = isPluginEnabledForAllProjects;
          });

          const isAppVisibleInAllIssuesForAllProjects =
            this.getIsAppVisibleInAllIssuesForGivenProjects(filteredProjects);

          this.setState({
            filteredProjects: filteredProjects,
            isPluginEnabledForAllProjects,
            isAppVisibleInAllIssuesForAllProjects,
            loading: false,
          });
        } catch (e) {
          StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
            e,
            messages['error.setEnabledForFiltered.message']
          );
        }
      }
    );
  };

  reloadSettings = async () => {
    try {
      this.allProjectsWithSettings = await SettingsService.getSettings();
      const isJiraAdminUser = await PermissionService.isJiraAdminUser();
      const filteredProjects = this.getFilteredProjects(this.state.query);
      const isAppVisibleInAllIssuesForAllProjects =
        this.getIsAppVisibleInAllIssuesForGivenProjects(filteredProjects);
      const isPluginEnabledForAllProjects =
        this.getIsPluginEnabledForGivenProjects(filteredProjects);

      this.setState({
        filteredProjects,
        isAppVisibleInAllIssuesForAllProjects,
        isPluginEnabledForAllProjects,
        loading: false,
        isJiraAdminUser,
        isInitialized: true,
      });
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.getSettings.message']
      );
    }
  };

  doesUserHasRightsToSeeSettingsByProjects() {
    return (
      !Utils.isNilOrEmpty(this.allProjectsWithSettings) ||
      this.state.isJiraAdminUser
    );
  }

  addTestCaseIssueTypeToProjectScheme = async (projectId: string) => {
    this.setState({
      loading: true,
    });

    try {
      await StorageOfUniqueServices.get(
        'QAlityIssueTypeService'
      ).addTestCaseIssueTypeToProjectScheme(projectId);
      this.reloadSettings();
    } catch (e) {
      this.setState({
        loading: false,
      });
      throw e;
    }
  };

  removeTestCaseIssueTypeFromProjectScheme = async (pojectId: string) => {
    this.setState({
      loading: true,
    });

    try {
      await StorageOfUniqueServices.get(
        'QAlityIssueTypeService'
      ).removeTestCaseIssueTypeFromProjectScheme(pojectId);
      this.reloadSettings();
    } catch (e) {
      this.setState({
        loading: false,
      });
      throw e;
    }
  };

  getIsAppVisibleInAllIssuesForGivenProjects = (projects: Array<any>) => {
    return projects
      .filter((p) => p.isPluginEnabled)
      .every((p) => p.isAppVisibleInAllIssues);
  };

  getIsPluginEnabledForGivenProjects = (projects: Array<any>) => {
    return projects.every((p) => p.isPluginEnabled);
  };

  getIsPluginEnabledInAtLeastOneProject = () => {
    return (
      this.state.isPluginEnabledForAllProjects ||
      this.state.filteredProjects.some((project) => project.isPluginEnabled)
    );
  };

  render() {
    const { children } = this.props;

    return (
      <QAlitySettingsContext.Provider
        value={{
          hasRightsToSeeSettings:
            this.doesUserHasRightsToSeeSettingsByProjects(),
          isJiraAdminUser: this.state.isJiraAdminUser,
          isAppVisibleInAllIssuesForAllProjects:
            this.state.isAppVisibleInAllIssuesForAllProjects,
          isPluginEnabledForAllProjects:
            this.state.isPluginEnabledForAllProjects,
          getIsPluginEnabledInAtLeastOneProject:
            this.getIsPluginEnabledInAtLeastOneProject,
          projects: this.state.filteredProjects,
          loading: this.state.loading,
          isInitialized: this.state.isInitialized,
          query: this.state.query,
          togglePluginInProject: this.togglePluginInProject,
          toggleAppVisibilityInAllIssues: this.toggleAppVisibilityInAllIssues,
          onFilterChange: this.onFilterChange,
          clearFilter: this.clearFilter,
          toggleAppVisibilityInAllIssuesForAllProjects:
            this.toggleAppVisibilityInAllIssuesForAllProjects,
          togglePluginForAllProjects: this.togglePluginForAllProjects,
          addTestCaseIssueTypeToProjectScheme:
            this.addTestCaseIssueTypeToProjectScheme,
          removeTestCaseIssueTypeFromProjectScheme:
            this.removeTestCaseIssueTypeFromProjectScheme,
        }}
      >
        {children}
      </QAlitySettingsContext.Provider>
    );
  }
}

export const QAlitySettingsContextConsumer = QAlitySettingsContext.Consumer;
